"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Subtle gradient mesh that follows the cursor on desktop.
 *
 * Design intent: barely-there ambient depth, not a showpiece. Two radial
 * gradients in light/dark palettes, positioned by mouse with heavy easing.
 * Opacity is intentionally low — we want the hero content to own attention,
 * not the background.
 *
 * Performance:
 *  - `pointer-events: none` (never blocks clicks).
 *  - Deferred mount via requestIdleCallback so it doesn't touch LCP.
 *  - `prefers-reduced-motion` → static centered gradient, no tracking.
 *  - rAF-throttled mouse follow (~60fps without thrashing state).
 */
export default function HeroV3Background() {
  const containerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const [mounted, setMounted] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    // Defer mount — no work during LCP window.
    const schedule =
      typeof window.requestIdleCallback === "function"
        ? window.requestIdleCallback
        : (cb: IdleRequestCallback) => setTimeout(() => cb({} as IdleDeadline), 200);
    const id = schedule(() => setMounted(true));
    return () => {
      if (typeof window.cancelIdleCallback === "function" && typeof id === "number") {
        window.cancelIdleCallback(id);
      }
    };
  }, []);

  useEffect(() => {
    if (!mounted) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, [mounted]);

  useEffect(() => {
    if (!mounted || reducedMotion) return;
    const el = containerRef.current;
    if (!el) return;

    let targetX = 50;
    let targetY = 35;
    let currentX = 50;
    let currentY = 35;

    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      targetX = ((e.clientX - rect.left) / rect.width) * 100;
      targetY = ((e.clientY - rect.top) / rect.height) * 100;
    };

    const tick = () => {
      currentX += (targetX - currentX) * 0.05;
      currentY += (targetY - currentY) * 0.05;
      el.style.setProperty("--mx", `${currentX}%`);
      el.style.setProperty("--my", `${currentY}%`);
      rafRef.current = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", onMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [mounted, reducedMotion]);

  return (
    <div
      ref={containerRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{
        // Defaults used until JS sets them — also the values used on reduced-motion.
        ["--mx" as string]: "50%",
        ["--my" as string]: "35%",
      }}
    >
      {/* Primary mesh — neutral cool white that tracks the cursor.
          Same spatial trick as before, just not green. */}
      <div
        className="absolute inset-0 opacity-70 dark:opacity-100 transition-opacity"
        style={{
          background:
            "radial-gradient(800px 600px at var(--mx) var(--my), rgba(255, 255, 255, 0.06), transparent 60%)",
        }}
      />
      {/* Secondary mesh — subtle slate accent, bottom-right. */}
      <div
        className="absolute inset-0 opacity-50"
        style={{
          background:
            "radial-gradient(1000px 700px at 80% 90%, rgba(148, 163, 184, 0.06), transparent 60%)",
        }}
      />
      {/* Very light grid — barely visible, Linear-style gridlines */}
      <div
        className="absolute inset-0 opacity-[0.025] dark:opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse 90% 60% at 50% 40%, black, transparent)",
          WebkitMaskImage:
            "radial-gradient(ellipse 90% 60% at 50% 40%, black, transparent)",
          color: "rgb(0 0 0)",
        }}
      />

      {/* Bottom fade */}
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-white dark:from-[#0a0a0b] to-transparent" />
    </div>
  );
}
